// apiAction's longest default retry backoff is mobile:
// 200+400+800+1600+3200 = 6,200ms. H-AT1 finance wrappers impose a 60s
// end-to-end timeout, safely above that delay budget plus normal network
// latency. Cross-tab snapshots get 120s freshness, leaving another 60s for
// observation/cleanup after the hard request lifetime. Same-runtime active IDs
// remain active until finish/supersession; TTL only classifies unknown tabs.
export const FINANCE_ATOMIC_ACTION_TIMEOUT_MS = 60_000;
export const ATOMIC_PENDING_FRESHNESS_TTL_MS = 120_000;
export const MAX_FINISHED_ATOMIC_REQUEST_IDS = 2_048;

const atomicRequestStates = new Map();

function isValidRequestId(requestId) {
  return typeof requestId === 'string' && requestId.length > 0;
}

function pruneAtomicRequestStates(now) {
  for (const [requestId, entry] of atomicRequestStates) {
    if (
      entry.state === 'finished' &&
      now - entry.updatedAt > ATOMIC_PENDING_FRESHNESS_TTL_MS
    ) {
      atomicRequestStates.delete(requestId);
    }
  }

  let finishedCount = 0;
  for (const entry of atomicRequestStates.values()) {
    if (entry.state === 'finished') finishedCount += 1;
  }
  if (finishedCount <= MAX_FINISHED_ATOMIC_REQUEST_IDS) return;

  for (const [requestId, entry] of atomicRequestStates) {
    if (entry.state !== 'finished') continue;
    atomicRequestStates.delete(requestId);
    finishedCount -= 1;
    if (finishedCount <= MAX_FINISHED_ATOMIC_REQUEST_IDS) break;
  }
}

export function registerActiveAtomicRequest(requestId, now = Date.now()) {
  if (!isValidRequestId(requestId)) return;
  pruneAtomicRequestStates(now);
  atomicRequestStates.delete(requestId);
  atomicRequestStates.set(requestId, { state: 'active', updatedAt: now });
}

export function markAtomicRequestFinished(requestId, now = Date.now()) {
  if (!isValidRequestId(requestId)) return;
  pruneAtomicRequestStates(now);
  atomicRequestStates.delete(requestId);
  atomicRequestStates.set(requestId, { state: 'finished', updatedAt: now });
  pruneAtomicRequestStates(now);
}

export function getAtomicRequestRuntimeState(requestId, now = Date.now()) {
  if (!isValidRequestId(requestId)) return 'unknown';
  pruneAtomicRequestStates(now);
  return atomicRequestStates.get(requestId)?.state || 'unknown';
}

export function isAtomicRequestActive(requestId) {
  return getAtomicRequestRuntimeState(requestId) === 'active';
}

export function _activeAtomicRequestCountForTests() {
  let count = 0;
  for (const entry of atomicRequestStates.values()) {
    if (entry.state === 'active') count += 1;
  }
  return count;
}

export function _atomicRequestRegistrySizeForTests() {
  return atomicRequestStates.size;
}

export function _resetActiveAtomicRequestsForTests() {
  atomicRequestStates.clear();
}
